import { useEffect, useMemo, useState } from 'react'
import Alert from 'core/components/Alert'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { convert, type Unit } from 'core/components/SizeInputWithUnit'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import Info from 'core/components/validatedForm/Info'
import QuantitySelector from 'core/components/validatedForm/QuantitySelectorField'
import TextField from 'core/components/validatedForm/TextField'
import Button from 'core/elements/button'
import Dropdown from 'core/elements/dropdown'
import Text from 'core/elements/Text'
import { requiredValidator } from 'core/utils/fieldValidators'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'

const ramOptions = [
  { label: 'MiB', value: 'MiB' as Unit },
  { label: 'GiB', value: 'GiB' as Unit },
]

const diskOptions = [
  { label: 'GiB', value: 'GiB' as Unit },
  { label: 'TiB', value: 'TiB' as Unit },
]

export interface CustomFlavorSpec {
  vcpus: number
  // RAM in MiB (the unit Nova stores flavors in)
  ram: number
  // Disk in GiB, matching this codebase's existing flavor-disk convention (see CreateFlavorModal)
  disk: number
  saveFlavor: boolean
  flavorName: string
}

export const defaultCustomFlavorSpec: CustomFlavorSpec = {
  vcpus: 1,
  ram: 1024,
  disk: 1,
  saveFlavor: false,
  flavorName: '',
}

interface Props {
  flavors: any[]
  value: CustomFlavorSpec
  onChange: (patch: Partial<CustomFlavorSpec>) => void
  // CFF-FR-003: switches the parent FlavorStep to "Choose from available flavors" mode with
  // this flavor pre-selected, instead of creating a duplicate.
  onUseExistingFlavor: (flavor: any) => void
  // CFF-FR-006: volume-backed sources boot from the attached volume, so the flavor's own disk
  // must be 0 — same rule FlavorStep already applies to the existing-flavor list/grid.
  isVolumeSourceType?: boolean
}

// CFF-FR-002: find a tenant-visible flavor with the exact same vCPUs/RAM(MiB)/Disk(GiB).
const findMatchingFlavor = (value: CustomFlavorSpec, flavors: any[]) => {
  const { vcpus, ram, disk } = value
  if (!vcpus || !ram || disk == null || !flavors?.length) {
    return null
  }
  return (
    flavors.find(
      (flavor) =>
        Number(flavor.vcpus) === Number(vcpus) &&
        Number(flavor.ram) === Number(ram) &&
        Number(flavor.disk) === Number(disk),
    ) ?? null
  )
}

export default function CustomFlavorForm({
  flavors,
  value,
  onChange,
  onUseExistingFlavor,
  isVolumeSourceType = false,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()
  const { vcpus, ram, disk, saveFlavor, flavorName } = value
  const [ramUnit, setRamUnit] = useState<Unit>('GiB')
  const [diskUnit, setDiskUnit] = useState<Unit>('GiB')

  // CFF-FR-006: the attached volume supplies the root disk, so force disk to 0 the moment the
  // source becomes volume-backed (mirrors the existing-flavor list's zero-disk-only filter).
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on isVolumeSourceType changes, not on every disk/onChange identity change
  useEffect(() => {
    if (isVolumeSourceType && disk !== 0) {
      onChange({ disk: 0 })
    } else if (!isVolumeSourceType && disk === 0) {
      onChange({ disk: 1 })
    }
  }, [isVolumeSourceType])

  const matchedFlavor = useMemo(() => findMatchingFlavor(value, flavors), [value, flavors])

  return (
    <div className={classes.fields}>
      <Alert
        variant="primary"
        title="Custom Flavors"
        message="Size the VM for this deployment only. The flavor is created as private. GPU, vTPM, custom metadata and host aggregates are not available here. If you need aggregate pinning or vTPM, choose an existing flavor."
      />
      {!!matchedFlavor && (
        <Alert
          variant="warning"
          title="Flavor already exists"
          message={`A flavor with this exact size already exists: ${matchedFlavor.name}. There's no need to create a temporary flavor. You can use the existing one.`}
        >
          <Button
            className={classes.useFlavorButton}
            variant="secondary"
            onClick={() => onUseExistingFlavor(matchedFlavor)}
          >
            Use flavor {matchedFlavor.name}
          </Button>
        </Alert>
      )}
      <div className={classes.quantityFieldsRow}>
        <div className={classes.quantityFieldGroup}>
          <Text variant="caption1">vCPUs*</Text>
          <QuantitySelector
            id="customFlavorVcpus"
            value={vcpus}
            min={1}
            onChange={(val) => onChange({ vcpus: val })}
          />
        </div>
        <div className={classes.quantityFieldGroup}>
          <Text variant="caption1">RAM*</Text>
          <div className={classes.quantityWithUnit}>
            <QuantitySelector
              key={ramUnit}
              id="customFlavorRam"
              className={classes.quantityStepperFixed}
              value={convert(ram, 'MiB', ramUnit)}
              min={1}
              onChange={(val) => onChange({ ram: convert(val, ramUnit, 'MiB') })}
            />
            <div className={classes.unitDropdownWrapper}>
              <Dropdown
                name="customFlavorRamUnit"
                items={ramOptions}
                value={ramUnit}
                onChange={setRamUnit}
              />
            </div>
          </div>
        </div>
        <div className={classes.quantityFieldGroup}>
          <Text variant="caption1">Disk{!isVolumeSourceType && '*'}</Text>
          <div className={classes.quantityWithUnit}>
            <QuantitySelector
              key={diskUnit}
              id="customFlavorDisk"
              className={classes.quantityStepperFixed}
              value={isVolumeSourceType ? 0 : convert(disk, 'GiB', diskUnit)}
              min={isVolumeSourceType ? 0 : 1}
              disabled={isVolumeSourceType}
              onChange={(val) => onChange({ disk: convert(val, diskUnit, 'GiB') })}
            />
            <div className={classes.unitDropdownWrapper}>
              <Dropdown
                name="customFlavorDiskUnit"
                items={diskOptions}
                value={diskUnit}
                disabled={isVolumeSourceType}
                onChange={setDiskUnit}
              />
            </div>
          </div>
        </div>
      </div>
      <Info className={classes.info}>
        <CheckboxField
          id="saveFlavor"
          label="Save this flavor"
          value={saveFlavor}
          onChange={(checked?: boolean) =>
            onChange({ saveFlavor: !!checked, flavorName: checked ? flavorName : '' })
          }
        />
        <Text variant="body2" className={classes.cardText}>
          The flavor will be kept after the VM is created and appear under saved flavors.
        </Text>
        {saveFlavor && (
          <>
            <br />
            <TextField
              id="flavorName"
              label="Flavor Name"
              value={flavorName}
              onChange={(val: string) => onChange({ flavorName: val })}
              validations={[requiredValidator]}
              required
            />
          </>
        )}
        <div className={classes.separator} />
        <div className={classes.quantityWithUnit}>
          <FontAwesomeIcon size="md">info-circle</FontAwesomeIcon>
          <Text variant="body2" className={classes.cardText}>
            Leave unchecked for a one-off size. A temporary flavor is created for this deployment
            and deleted automatically after the VM is created.
          </Text>
        </div>
      </Info>
    </div>
  )
}
